/**
 * Unified Sonority Type (GRE-114)
 *
 * A single type for all vertical harmonies:
 * - User-created chords from the palette
 * - Analyzed chords from MusicXML/audio
 * - Library chords from presets
 * - AI-generated suggestions
 *
 * This unifies the previously separate Chord and AnalyzedChord types.
 */

import type {
  Chord,
  ChordQuality,
  ChordExtensions,
  ChordSource,
  ChromaticType,
  Mode,
  MusicalKey,
  Position,
  ScaleDegree,
  Voices,
} from './chord';
import type { SonorityQuality } from './harmonic-vocabulary';
import type { MagicMoment } from './magic-moment';

// ============================================================================
// Origin and Context Types
// ============================================================================

/**
 * Origin of the sonority data - more granular than ChordSource
 */
export type SonorityOrigin =
  | 'user'           // Created by user via palette
  | 'analyzed'       // Detected from MusicXML/audio
  | 'library'        // Loaded from preset/library
  | 'ai_generated';  // Created by AI suggestion (refine, surprise me)

/**
 * Repertoire period for analysis routing
 * Determines which analysis strategy to use
 */
export type RepertoirePeriod =
  | 'renaissance'    // Palestrina, Victoria - intervallic analysis, magic moments
  | 'baroque'        // Bach - figured bass + Roman numerals
  | 'classical'      // Mozart, Haydn - Roman numerals
  | 'romantic'       // Chopin, Brahms - chromatic extensions
  | 'jazz'           // Extended chords, alterations
  | 'contemporary';  // Modern choral (Whitacre, Lauridsen)

// ============================================================================
// Analysis Types
// ============================================================================

/**
 * Core analysis result (from backend or computed)
 * Captures what was detected in the vertical slice
 */
export interface SonorityAnalysis {
  // Bass and root (may differ for inversions)
  bass: string;                         // Pitch name: "C", "F#"
  root: string | null;                  // Inferred root (null if unclear)

  // Quality - Renaissance uses SonorityQuality, modern uses ChordQuality
  quality: SonorityQuality | ChordQuality;

  // Intervals from bass (semitones)
  intervalsFromBass: number[];

  // Intervals from root (semitones mod 12) - only if root was determined
  intervalsFromRoot?: number[];

  // Completeness
  isComplete: boolean;                  // Has all expected chord tones

  // Confidence in analysis (0.0-1.0)
  confidence: number;

  // Whether harmony was inferred from sparse data
  impliedHarmony?: boolean;

  // Raw detected pitch classes (0-11)
  pitchClasses?: number[];
}

// ============================================================================
// Unified Sonority Interface
// ============================================================================

/**
 * Unified Sonority - the master type for all harmonies
 *
 * This interface can represent:
 * - A user-created chord from the palette
 * - An analyzed chord from MusicXML import
 * - A chord from the library
 * - An AI-generated suggestion
 *
 * The goal is for all these to look and behave identically in the UI.
 */
export interface Sonority {
  // ---- Identity ----
  id: string;

  // ---- Origin and Source ----
  origin: SonorityOrigin;
  sourceChordId?: string;               // If transformed from existing Chord

  // ---- Core Musical Data ----
  scaleDegree?: ScaleDegree;            // 1-7 for diatonic (optional for analyzed)
  quality: ChordQuality;                // Normalized to ChordQuality for UI
  extensions: ChordExtensions;

  // ---- Key Context ----
  key: MusicalKey;
  mode: Mode;

  // ---- Chromatic Flags ----
  isChromatic: boolean;
  chromaticType?: ChromaticType;

  // ---- Voicing ----
  voices: Voices;
  voicesCustomized?: boolean;           // True if user manually edited voice leading

  // ---- Timeline Position ----
  startBeat: number;
  duration: number;

  // ---- Canvas Position (for UI) ----
  position: Position;
  size: number;

  // ---- Selection/Playback State (transient) ----
  selected: boolean;
  playing: boolean;

  // ---- Analysis Metadata ----
  analysis?: SonorityAnalysis;          // From backend or computed

  // ---- Magic Moments ----
  magicMoments?: MagicMoment[];         // Suspensions, false relations, etc.

  // ---- Timestamps ----
  createdAt: string;
  updatedAt?: string;

  // ---- User Annotation ----
  annotation?: string;
}

// ============================================================================
// Conversion Utilities
// ============================================================================

/**
 * Convert legacy Chord to Sonority
 *
 * This allows gradual migration - existing code can continue using Chord,
 * while new code can work with Sonority.
 */
export function chordToSonority(chord: Chord): Sonority {
  const origin: SonorityOrigin =
    chord.source === 'analyzed' ? 'analyzed' :
    chord.source === 'library' ? 'library' : 'user';

  return {
    id: chord.id,
    origin,
    scaleDegree: chord.scaleDegree,
    quality: chord.quality,
    extensions: chord.extensions,
    key: chord.key,
    mode: chord.mode,
    isChromatic: chord.isChromatic,
    chromaticType: chord.chromaticType,
    voices: chord.voices,
    voicesCustomized: chord.voicesCustomized,
    startBeat: chord.startBeat,
    duration: chord.duration,
    position: chord.position,
    size: chord.size,
    selected: chord.selected,
    playing: chord.playing,
    createdAt: chord.createdAt,
    annotation: chord.annotation,
  };
}

/**
 * Convert Sonority back to legacy Chord (for backward compatibility)
 *
 * This ensures existing code that expects Chord continues to work
 * during the migration period.
 */
export function sonorityToChord(sonority: Sonority): Chord {
  const source: ChordSource =
    sonority.origin === 'analyzed' ? 'analyzed' :
    sonority.origin === 'library' ? 'library' : 'user';

  return {
    id: sonority.id,
    scaleDegree: sonority.scaleDegree ?? 1,
    quality: sonority.quality,
    extensions: sonority.extensions,
    key: sonority.key,
    mode: sonority.mode,
    isChromatic: sonority.isChromatic,
    chromaticType: sonority.chromaticType,
    voices: sonority.voices,
    voicesCustomized: sonority.voicesCustomized,
    startBeat: sonority.startBeat,
    duration: sonority.duration,
    position: sonority.position,
    size: sonority.size,
    selected: sonority.selected,
    playing: sonority.playing,
    source,
    createdAt: sonority.createdAt,
    annotation: sonority.annotation,
  };
}

/**
 * Convert an array of Chords to Sonorities
 */
export function chordsToSonorities(chords: Chord[]): Sonority[] {
  return chords.map(chordToSonority);
}

/**
 * Convert an array of Sonorities to Chords
 */
export function sonoritiesToChords(sonorities: Sonority[]): Chord[] {
  return sonorities.map(sonorityToChord);
}

// ============================================================================
// Helper Types
// ============================================================================

/**
 * Helper type for creating a new Sonority (with optional fields)
 */
export type SonorityInput = Partial<Sonority> &
  Pick<Sonority, 'quality' | 'key' | 'mode'>;

/**
 * Sonority with required analysis (for analyzed content)
 */
export type AnalyzedSonority = Sonority & {
  origin: 'analyzed';
  analysis: SonorityAnalysis;
};

/**
 * Type guard for analyzed sonorities
 */
export function isAnalyzedSonority(sonority: Sonority): sonority is AnalyzedSonority {
  return sonority.origin === 'analyzed' && sonority.analysis !== undefined;
}
